<template>
	<div>
		<div class="d-flex align-items-center mb-2">
			<span class="fw-semibold">{{
				order ? `Work Order ${order.id}` : "New Work Order"
			}}</span>
			<span v-if="order" class="badge ms-2" :class="stateBadge">{{
				order.state
			}}</span>
		</div>

		<div class="mb-2">
			<label class="form-label small mb-1">Description</label>
			<textarea
				v-model="form.description"
				class="form-control form-control-sm"
				rows="3"
				placeholder="What needs to happen"
			></textarea>
		</div>
		<div class="row g-2 mb-2">
			<div class="col-6">
				<label class="form-label small mb-1">Priority</label>
				<select v-model="form.priority" class="form-select form-select-sm">
					<option value="high">High</option>
					<option value="normal">Normal</option>
					<option value="low">Low</option>
				</select>
			</div>
			<div class="col-6">
				<label class="form-label small mb-1">Due</label>
				<input
					v-model="form.due"
					type="date"
					class="form-control form-control-sm"
				/>
			</div>
		</div>
		<div class="mb-2">
			<label class="form-label small mb-1">Requirements (free text)</label>
			<textarea
				v-model="form.requirements"
				class="form-control form-control-sm"
				rows="2"
				placeholder="Equipment, access, qualification, materials"
			></textarea>
		</div>
		<div class="d-flex align-items-center justify-content-between mb-1">
			<label class="form-label small mb-0">H3 cells (optional)</label>
			<span class="small text-muted">{{ draftCells.length }}</span>
		</div>
		<div
			class="d-flex flex-wrap gap-1 mb-2"
			style="max-height: 6rem; overflow: auto"
		>
			<span v-for="cell in draftCells" :key="cell" class="badge text-bg-light">
				{{ cell }}
				<button class="btn btn-sm p-0 ms-1" @click="emit('remove-cell', cell)">
					<i class="bi bi-x"></i>
				</button>
			</span>
		</div>
		<div class="form-text mb-2">Click the map to add a cell.</div>

		<div
			v-if="order && order.origin !== 'manual'"
			class="small text-muted mb-2"
		>
			Origin: {{ order.origin }}
			<span v-if="order.origin_lead_id">
				· Lead {{ order.origin_lead_id }} (context only)</span
			>
		</div>

		<div class="d-flex gap-2">
			<button
				class="btn btn-sm btn-primary"
				:disabled="saving || saveBlockers.length > 0"
				@click="save"
			>
				{{
					saving ? "Saving…" : order ? "Save Work Order" : "Create Work Order"
				}}
			</button>
			<button
				v-if="order && order.state === 'open'"
				class="btn btn-sm btn-outline-danger ms-auto"
				:disabled="saving"
				@click="cancel"
			>
				Cancel…
			</button>
		</div>

		<!-- Why the save button is disabled, stated here rather than as a 400
		     afterwards. -->
		<div
			v-if="saveBlockers.length > 0"
			class="alert alert-warning py-2 px-2 mt-2 mb-0"
			role="status"
		>
			<div class="fw-semibold small mb-1">
				Before this
				{{ order ? "Work Order can change" : "Work Order can be created" }}
			</div>
			<ul class="small mb-0 ps-3">
				<li v-for="blocker in saveBlockers" :key="blocker.key">
					{{ blocker.message }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";

import type { WorkOrderDetail } from "@/type/api";

const props = defineProps<{
	draftCells: string[];
	/** The resolution the draft's cells are cut at, decided once by the grid. */
	resolution: number;
	order: WorkOrderDetail | null;
	saving: boolean;
}>();

const emit = defineEmits<{
	(e: "remove-cell", cell: string): void;
	(e: "save", payload: Record<string, unknown>): void;
	(e: "cancel", reason: string): void;
}>();

const form = reactive({
	description: "",
	priority: "normal",
	due: "",
	requirements: "",
});

watch(
	() => props.order,
	(order) => {
		form.description = order?.description ?? "";
		form.priority = order?.priority ?? "normal";
		form.due = order?.due_at ? order.due_at.slice(0, 10) : "";
		form.requirements = order?.requirements ?? "";
	},
	{ immediate: true },
);

const stateBadge = computed(() =>
	props.order?.state === "cancelled" ? "text-bg-danger" : "text-bg-success",
);

/** One reason the save button is disabled. */
interface SaveBlocker {
	key: string;
	message: string;
}

/** Every reason the save button is disabled. Description is the server's one
 * required field; priority, due, requirements, and cells are optional. */
const saveBlockers = computed<SaveBlocker[]>(() => {
	const blockers: SaveBlocker[] = [];
	if (form.description.trim() === "") {
		blockers.push({
			key: "description",
			message:
				"A Work Order needs a description: fill in the Description field.",
		});
	}
	return blockers;
});

function save() {
	emit("save", {
		description: form.description.trim(),
		priority: form.priority,
		due_at: form.due ? new Date(form.due + "T00:00:00Z").toISOString() : null,
		requirements: form.requirements || null,
		h3_resolution: props.resolution,
		cells: props.draftCells,
	});
}

function cancel() {
	const reason = window.prompt("Reason for cancelling this Work Order");
	if (reason && reason.trim()) emit("cancel", reason.trim());
}
</script>
